import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface CartItem {
  /** 行唯一标识：同规格可多行（不同备注） */
  lineId: string;
  productId: string;
  name: string;
  price: number;
  unit: string;
  image: string;
  quantity: number;
  stock: number;
  model: string;
  specId?: string;
  specName?: string;
  remark?: string;
  /** 清单外规格 */
  isOutside?: boolean;
}

export interface CartActionOptions {
  /** Anchor element for stock tip near the clicked button */
  anchor?: Element | EventTarget | null;
}

interface CartContextType {
  items: CartItem[];
  addItem: (
    item: Omit<CartItem, 'quantity' | 'remark' | 'lineId'> & { quantity?: number },
    options?: CartActionOptions,
  ) => void;
  /** 复制当前行，新增一条同规格空备注行（数量默认 1） */
  duplicateItem: (lineId: string, options?: CartActionOptions) => void;
  removeItem: (lineId: string) => void;
  removeItems: (lineIds: string[]) => void;
  /** 按行 lineId 移除购物车行 */
  removeLines: (lineIds: string[]) => void;
  updateQuantity: (lineId: string, quantity: number, options?: CartActionOptions) => void;
  updateRemark: (lineId: string, remark: string) => void;
  clearCart: () => void;
  totalCount: number;
  totalAmount: number;
}

const CartContext = createContext<CartContextType | null>(null);
const CART_STORAGE_KEY = 'cart';

/** stock < 0 means unlimited; only validate when stock >= 0 */
function shouldValidateStock(stock: number) {
  return Number.isFinite(stock) && stock >= 0;
}

function tipStockInsufficient(anchor?: Element | EventTarget | null) {
  void import('@/utils/message').then(({ toastWarning }) => {
    toastWarning('库存不足', anchor);
  });
}

function createLineId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Returns null when item should be removed from cart */
function normalizeQuantity(quantity: number, stock: number): number | null {
  const qty = Math.floor(Number(quantity));
  if (!Number.isFinite(qty) || qty < 1) return null;
  if (!shouldValidateStock(stock)) return qty;
  const max = Math.floor(stock);
  if (max < 1) return null;
  return Math.min(qty, max);
}

function persistCart(items: CartItem[]) {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

function sameSpec(a: CartItem, productId: string, specId?: string) {
  return a.productId === productId && (a.specId || '') === (specId || '');
}

function sanitizeCartItems(raw: unknown): CartItem[] {
  if (!Array.isArray(raw)) return [];
  const cleaned: CartItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Partial<CartItem>;
    if (!item.productId) continue;
    const qty = normalizeQuantity(Number(item.quantity), Number(item.stock));
    if (qty == null) continue;
    cleaned.push({
      lineId: item.lineId || createLineId(),
      productId: String(item.productId),
      name: String(item.name || ''),
      price: Number(item.price) || 0,
      unit: String(item.unit || ''),
      image: String(item.image || ''),
      quantity: qty,
      stock: Number(item.stock),
      model: String(item.model || ''),
      specId: item.specId,
      specName: item.specName,
      remark: item.remark || '',
      isOutside: item.isOutside === true,
    });
  }
  return cleaned;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      const cleaned = sanitizeCartItems(saved ? JSON.parse(saved) : []);
      persistCart(cleaned);
      return cleaned;
    } catch {
      return [];
    }
  });

  const addItem = useCallback(
    (
      item: Omit<CartItem, 'quantity' | 'remark' | 'lineId'> & { quantity?: number },
      options?: CartActionOptions,
    ) => {
      const anchor = options?.anchor;
      const addQty = Math.max(1, Math.floor(Number(item.quantity) || 1));

      setItems((prev) => {
        const stock = Number(item.stock);
        const sameSpecQty = prev
          .filter((i) => sameSpec(i, item.productId, item.specId))
          .reduce((sum, i) => sum + (i.quantity > 0 ? i.quantity : 0), 0);

        if (shouldValidateStock(stock) && sameSpecQty >= stock) {
          tipStockInsufficient(anchor);
          return prev;
        }

        let nextAdd = addQty;
        if (shouldValidateStock(stock) && sameSpecQty + nextAdd > stock) {
          nextAdd = stock - sameSpecQty;
          if (nextAdd < 1) {
            tipStockInsufficient(anchor);
            return prev;
          }
          tipStockInsufficient(anchor);
        }

        const existingIndex = prev.findIndex((i) => sameSpec(i, item.productId, item.specId));
        let next: CartItem[];

        if (existingIndex >= 0) {
          // 默认合并同规格数量到首次出现的行
          next = prev.map((row, idx) =>
            idx === existingIndex
              ? {
                  ...row,
                  ...item,
                  lineId: row.lineId,
                  stock,
                  quantity: row.quantity + nextAdd,
                  remark: row.remark || '',
                }
              : row,
          );
        } else {
          const nextQty = normalizeQuantity(nextAdd, stock);
          if (nextQty == null) {
            tipStockInsufficient(anchor);
            return prev;
          }
          next = [
            ...prev,
            {
              ...item,
              lineId: createLineId(),
              stock,
              quantity: nextQty,
              remark: '',
            },
          ];
        }

        persistCart(next);
        return next;
      });
    },
    [],
  );

  const duplicateItem = useCallback((lineId: string, options?: CartActionOptions) => {
    const anchor = options?.anchor;
    setItems((prev) => {
      const source = prev.find((i) => i.lineId === lineId);
      if (!source) return prev;

      const stock = Number(source.stock);
      const sameSpecQty = prev
        .filter((i) => sameSpec(i, source.productId, source.specId))
        .reduce((sum, i) => sum + (i.quantity > 0 ? i.quantity : 0), 0);

      if (shouldValidateStock(stock) && sameSpecQty >= stock) {
        tipStockInsufficient(anchor);
        return prev;
      }

      const nextQty = normalizeQuantity(1, stock);
      if (nextQty == null) {
        tipStockInsufficient(anchor);
        return prev;
      }
      if (shouldValidateStock(stock) && sameSpecQty + nextQty > stock) {
        tipStockInsufficient(anchor);
        return prev;
      }

      const sourceIndex = prev.findIndex((i) => i.lineId === lineId);
      const copied: CartItem = {
        productId: source.productId,
        name: source.name,
        price: source.price,
        unit: source.unit,
        image: source.image,
        stock,
        model: source.model,
        specId: source.specId,
        specName: source.specName,
        isOutside: source.isOutside === true,
        lineId: createLineId(),
        quantity: nextQty,
        remark: '',
      };
      const next = [...prev];
      next.splice(sourceIndex + 1, 0, copied);
      persistCart(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((lineId: string) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.lineId !== lineId);
      persistCart(next);
      return next;
    });
  }, []);

  const removeItems = useCallback((lineIds: string[]) => {
    const idSet = new Set(lineIds.map(String));
    setItems((prev) => {
      const next = prev.filter((i) => !idSet.has(String(i.lineId)));
      persistCart(next);
      return next;
    });
  }, []);

  const removeLines = useCallback((lineIds: string[]) => {
    const idSet = new Set(lineIds.map(String));
    setItems((prev) => {
      const next = prev.filter((i) => !idSet.has(String(i.lineId)));
      persistCart(next);
      return next;
    });
  }, []);

  const updateQuantity = useCallback(
    (lineId: string, quantity: number, options?: CartActionOptions) => {
      const anchor = options?.anchor;
      setItems((prev) => {
        const target = prev.find((i) => i.lineId === lineId);
        if (!target) return prev;

        const stock = Number(target.stock);
        const requested = Math.floor(Number(quantity));
        const otherSameSpecQty = prev
          .filter((i) => i.lineId !== lineId && sameSpec(i, target.productId, target.specId))
          .reduce((sum, i) => sum + (i.quantity > 0 ? i.quantity : 0), 0);

        if (
          shouldValidateStock(stock) &&
          Number.isFinite(requested) &&
          requested >= 1 &&
          otherSameSpecQty + requested > stock
        ) {
          tipStockInsufficient(anchor);
        }

        let nextQty = normalizeQuantity(quantity, stock);
        if (
          nextQty != null &&
          shouldValidateStock(stock) &&
          otherSameSpecQty + nextQty > stock
        ) {
          nextQty = stock - otherSameSpecQty;
          if (nextQty < 1) nextQty = null;
        }

        const next =
          nextQty == null
            ? prev.filter((i) => i.lineId !== lineId)
            : prev.map((i) => (i.lineId === lineId ? { ...i, quantity: nextQty } : i));
        persistCart(next);
        return next;
      });
    },
    [],
  );

  const updateRemark = useCallback((lineId: string, remark: string) => {
    setItems((prev) => {
      const next = prev.map((i) => (i.lineId === lineId ? { ...i, remark } : i));
      persistCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  }, []);

  const totalCount = useMemo(
    () => items.reduce((sum, i) => sum + (i.quantity > 0 ? i.quantity : 0), 0),
    [items],
  );
  const totalAmount = useMemo(
    () => items.reduce((sum, i) => sum + (i.quantity > 0 ? i.price * i.quantity : 0), 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      duplicateItem,
      removeItem,
      removeItems,
      removeLines,
      updateQuantity,
      updateRemark,
      clearCart,
      totalCount,
      totalAmount,
    }),
    [
      items,
      addItem,
      duplicateItem,
      removeItem,
      removeItems,
      removeLines,
      updateQuantity,
      updateRemark,
      clearCart,
      totalCount,
      totalAmount,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
